<template>
  <section class="panel">
    <header class="panel-head">
      <h3><span class="fas fa-file-contract"></span>Contrats <span class="count">{{ activeBusinesses.length }}</span></h3>
      <span v-if="activeBusinesses.length > 0" class="panel-total" title="Chiffre d'affaires HT du dossier">{{ formatAmount(order.soldHtAmount) }} HT vendu</span>
    </header>

    <p v-if="order.businesses.length === 0" class="empty">Aucun contrat dans ce dossier.<br>Rattachez un devis à ce dossier depuis la page Prestations.</p>

    <article v-for="business in activeBusinesses" :key="business.id" class="contract" :class="{pending: business.isPending}">
      <div class="contract-head">
        <div class="contract-id">
          <span class="contract-number">{{ documentRef('D', business.number) }}</span>
          <span class="contract-title" :title="business.title">{{ business.title || 'Sans titre' }}</span>
        </div>
        <div class="contract-amount" :title="'TTC ' + formatAmount(business.ttcAmount)">
          <strong>{{ formatAmount(business.netHtAmount) }}</strong><span class="unit">HT</span>
          <span v-if="business.isPending" class="pending-tag">en attente</span>
        </div>
        <div class="contract-actions">
          <button type="button" class="button-icon-custom" title="Ouvrir le contrat" @click="goToBusiness(business)"><span class="fas fa-up-right-from-square"></span></button>
          <button type="button" class="button-icon-custom" title="Avoir, avenant, duplication..." @click="$refs.manageBusinessModal.openModal(business)"><span class="fas fa-ellipsis"></span></button>
        </div>
      </div>
      <p v-if="showClient(business)" class="contract-client"><span class="fas fa-user"></span>{{ business.clientName }}</p>

      <ol class="documents">
        <li v-for="document in contractDocuments(business)" :key="document.key" class="document" :class="document.type">
          <span class="doc-label">{{ document.label }}</span>
          <span class="doc-ref">{{ document.ref }}</span>
          <StatusBadge :status="document.status" :status-enum="document.statusEnum" />
          <button type="button" class="doc-download" title="Télécharger le PDF" @click="downloadPdf(business, document)"><span class="fas fa-download"></span></button>
        </li>
      </ol>
    </article>

    <div v-if="droppedBusinesses.length > 0" class="dropped">
      <button type="button" class="dropped-toggle" @click="selfShowDropped = !selfShowDropped">
        {{ droppedBusinesses.length }} devis annulé{{ droppedBusinesses.length > 1 ? 's' : '' }} ou refusé{{ droppedBusinesses.length > 1 ? 's' : '' }}
        <span class="fas" :class="selfShowDropped ? 'fa-chevron-up' : 'fa-chevron-down'"></span>
      </button>
      <ul v-if="selfShowDropped">
        <li v-for="business in droppedBusinesses" :key="business.id">
          <span class="contract-number">{{ documentRef('D', business.number) }}</span>
          <span class="dropped-title">{{ business.title }}</span>
          <StatusBadge :status="business.status" />
          <button type="button" class="button-icon-custom" title="Ouvrir le contrat" @click="goToBusiness(business)"><span class="fas fa-up-right-from-square"></span></button>
        </li>
      </ul>
    </div>
  </section>
  <ManageBusinessModal ref="manageBusinessModal" :callback="refreshCallback" />
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faChevronDown, faChevronUp, faDownload, faEllipsis, faFileContract, faUpRightFromSquare, faUser} from "@fortawesome/free-solid-svg-icons";
import router from "@/router";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import ManageBusinessModal from "@/components/modals/ManageBusinessModal.vue";
import {BusinessService} from "@/controllers/business/BusinessService";
import {formatAmount} from "@/purchase/utils";
import {documentRef} from "@/accounting/services/export/AccountingFormat";
import {contractDocuments, isDroppedBusiness} from "@/components/production/productionUtils";
library.add(faChevronDown, faChevronUp, faDownload, faEllipsis, faFileContract, faUpRightFromSquare, faUser);

// Panneau « Contrats » d'un dossier : chaque contrat avec ses documents (devis, acompte, facture, avoirs) et leur statut.
// refreshCallback recharge le dossier après une action sur un contrat (avoir, avenant, duplication).
export default {
  name: 'OrderContracts',
  components: {StatusBadge, ManageBusinessModal},
  props: ['order', 'refreshCallback'],
  data() {
    return {
      selfShowDropped: false,
    };
  },
  computed: {
    sortedBusinesses() {
      return [...this.order.businesses].sort((a, b) => a.number - b.number);
    },
    activeBusinesses() {
      return this.sortedBusinesses.filter(business => !isDroppedBusiness(business));
    },
    droppedBusinesses() {
      return this.sortedBusinesses.filter(business => isDroppedBusiness(business));
    },
  },
  methods: {
    formatAmount,
    contractDocuments,
    documentRef,
    // Le client n'est répété que lorsque le dossier regroupe plusieurs clients
    showClient(business) {
      return !!business.clientName && (this.order.clientNames ?? []).length > 1;
    },
    goToBusiness(business) {
      router.push("/businesses?number=" + business.number + "#openEditModal");
    },
    async downloadPdf(business, document) {
      await BusinessService.downloadPdf(business, document.type, document.creditNoteNumber);
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/components/production/panel.styl';

.contract
  margin-bottom 10px
  padding 12px 14px
  border 1px solid #e6eaf2
  border-radius 12px
  background #fbfcfe
  transition box-shadow .15s ease, border-color .15s ease

  &:hover
    border-color #cfd8ea
    box-shadow 0 2px 8px rgba(36, 57, 107, 0.08)

  &.pending
    border-style dashed
    background white

.contract-head
  display flex
  align-items center
  gap 12px

.contract-id
  flex 1
  display flex
  align-items baseline
  gap 10px
  min-width 0

.contract-number
  flex 0 0 auto
  font-weight 700
  color THEME_COLOR_PRIMARY

.contract-title
  min-width 0
  overflow hidden
  white-space nowrap
  text-overflow ellipsis
  font-weight 600

.contract-amount
  display flex
  align-items baseline
  gap 4px
  white-space nowrap

  strong
    font-size 16px

  .unit
    font-size 11px
    font-weight 700
    color #8a93a8

  .pending-tag
    margin-left 6px
    padding 0 7px
    border-radius 10px
    background #fff3cd
    font-size 11px
    font-weight 600

.contract-actions
  display flex
  gap 2px

.contract-client
  display flex
  align-items center
  gap 8px
  margin 4px 0 0
  font-size 13px
  color #6f7a93

  span
    font-size 11px

// Les documents d'un contrat, dans l'ordre de leur vie (devis -> acompte -> facture -> avoirs)
.documents
  display flex
  flex-wrap wrap
  gap 8px
  margin 12px 0 0
  padding 0
  list-style none

.document
  display flex
  align-items center
  gap 7px
  padding 5px 6px 5px 10px
  border 1px solid #e3e7ef
  border-radius 9px
  background white
  font-size 13px

  .doc-label
    font-weight 700
    color #4a5670

  .doc-ref
    padding 0 6px
    border-radius 5px
    background #f0f1f3
    font-size 12px
    font-weight 600
    color #6b7076

  &.creditNote
    border-color #f1d6d2
    .doc-label
      color #b5473b

.doc-download
  width 24px
  height 24px
  border none
  border-radius 6px
  background transparent
  color #7c8aa8
  cursor pointer

  &:hover
    background #e8edf8
    color THEME_COLOR_PRIMARY

.dropped
  margin-top 6px

  .dropped-toggle
    display flex
    align-items center
    gap 8px
    padding 4px 2px
    border none
    background none
    color #7c8aa8
    font-size 13px
    cursor pointer

    &:hover
      color THEME_COLOR_PRIMARY

  ul
    margin 6px 0 0
    padding 0
    list-style none

  li
    display flex
    align-items center
    gap 10px
    padding 6px 8px
    border-radius 8px
    font-size 13px
    color #7c8aa8

    &:hover
      background #f3f5fa

  .dropped-title
    flex 1
    min-width 0
    overflow hidden
    white-space nowrap
    text-overflow ellipsis
</style>
